import { useEffect, useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type ClavePublica, type DispositivoPush, type PreferenciasAvisos } from "@/lib/api";
import { rutaPreferencias } from "@/lib/avisos";
import { desuscribir, leerEntorno, suscribir } from "@/lib/navegadorPush";
import { estadoPush, RUTA_CLAVE_PUBLICA, RUTA_DISPOSITIVOS, rutaDispositivo, type EntornoPush } from "@/lib/push";
import { ETIQUETA_ROL, validarPasswordNueva } from "@/lib/usuarios";

/** Lógica de "Mi perfil": datos propios, cambio de contraseña y preferencias de avisos (sin JSX). */
export function usePerfilTab() {
  const { usuario, membresia } = useContextoApp();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetida, setRepetida] = useState("");
  const [intentado, setIntentado] = useState(false);
  const [exito, setExito] = useState(false);
  const accion = useAsyncAction(async (d: { actual: string; nueva: string }) => {
    await api.post("/api/perfil/password", d);
    return true;
  });
  const errorValidacion = actual === "" ? "Poné tu contraseña actual" : validarPasswordNueva(nueva, repetida);

  const esAdmin = membresia.rol === "admin";
  const recurso = useApiResource<PreferenciasAvisos>(esAdmin ? null : rutaPreferencias(membresia.fideicomiso_id));
  const [prefs, setPrefs] = useState<PreferenciasAvisos | null>(null);
  const [errorPrefs, setErrorPrefs] = useState<string | null>(null);
  useEffect(() => {
    if (recurso.data) setPrefs(recurso.data);
  }, [recurso.data]);

  /** Guarda al tocar: muestra el cambio enseguida y, si el backend falla, vuelve atrás. */
  const cambiarPreferencia = async (clave: keyof PreferenciasAvisos, valor: boolean) => {
    if (!prefs) return;
    const anterior = prefs;
    const nuevas = { ...prefs, [clave]: valor };
    setPrefs(nuevas);
    setErrorPrefs(null);
    try {
      setPrefs(await api.put<PreferenciasAvisos>(rutaPreferencias(membresia.fideicomiso_id), nuevas));
    } catch {
      setPrefs(anterior);
      setErrorPrefs("No se pudieron guardar tus avisos. Probá de nuevo.");
    }
  };

  // --- Avisos al celular ---
  const clave = useApiResource<ClavePublica>(esAdmin ? null : RUTA_CLAVE_PUBLICA);
  const dispositivos = useApiResource<DispositivoPush[]>(esAdmin ? null : RUTA_DISPOSITIVOS);
  const [entorno, setEntorno] = useState<EntornoPush | null>(null);
  const [errorCelular, setErrorCelular] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    if (!esAdmin) void leerEntorno().then(setEntorno);
  }, [esAdmin]);
  const registradoEnServidor = Boolean(entorno?.endpoint && dispositivos.data?.some((d) => d.endpoint === entorno.endpoint));
  const estadoCelular =
    entorno && !clave.loading && !dispositivos.loading ? estadoPush(entorno, Boolean(clave.data), registradoEnServidor) : null;

  const refrescar = async () => {
    setEntorno(await leerEntorno());
    dispositivos.recargar();
  };

  const conOcupado = async (f: () => Promise<void>, error: string) => {
    setOcupado(true);
    setErrorCelular(null);
    try {
      await f();
    } catch {
      setErrorCelular(error);
    } finally {
      setOcupado(false);
    }
  };

  const activarCelular = () =>
    conOcupado(async () => {
      await api.post(RUTA_DISPOSITIVOS, await suscribir(clave.data!.clave));
      await refrescar();
    }, "No se activaron los avisos. Si tu celular te preguntó, tocá Permitir.");

  const quitar = async (d: DispositivoPush) => {
    await api.delete(rutaDispositivo(d.id));
    if (d.endpoint === entorno?.endpoint) await desuscribir();
    await refrescar();
  };

  const desactivarCelular = () =>
    conOcupado(async () => {
      const endpoint = entorno?.endpoint;
      // Lista fresca: `dispositivos.data` puede estar desactualizado justo después de activar (la recarga
      // dispara pero no se espera en `refrescar`), y sin esto el propio dispositivo no se borra del servidor.
      const lista = await api.get<DispositivoPush[]>(RUTA_DISPOSITIVOS);
      const propio = lista.find((d) => d.endpoint === endpoint);
      if (propio) await api.delete(rutaDispositivo(propio.id));
      await desuscribir();
      await refrescar();
    }, "No se pudieron desactivar los avisos. Probá de nuevo.");

  const quitarDispositivo = (d: DispositivoPush) => conOcupado(() => quitar(d), "No se pudo quitar el dispositivo. Probá de nuevo.");

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setIntentado(true);
    setExito(false);
    if (errorValidacion) return;
    if (await accion.run({ actual, nueva })) {
      setActual("");
      setNueva("");
      setRepetida("");
      setIntentado(false);
      setExito(true);
    }
  };

  return {
    nombre: usuario.nombre,
    email: usuario.email,
    rol: ETIQUETA_ROL[membresia.rol],
    actual,
    setActual,
    nueva,
    setNueva,
    repetida,
    setRepetida,
    error: (intentado ? errorValidacion : null) ?? accion.error,
    exito,
    enviar,
    guardando: accion.loading,
    mostrarAvisos: !esAdmin,
    prefs,
    cambiarPreferencia,
    errorPrefs,
    estadoCelular,
    activarCelular,
    desactivarCelular,
    quitarDispositivo,
    dispositivos: dispositivos.data ?? [],
    errorCelular,
    ocupadoCelular: ocupado,
  };
}
